import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Typography,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Button,
  Chip,
  IconButton,
  Divider,
  Alert,
  Collapse,
} from '@mui/material'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import {
  Close as CloseIcon,
  DragIndicator as DragIndicatorIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material'
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from '@hello-pangea/dnd'
import {
  GuidanceScriptData,
  GuidanceScript,
} from '@/apps/legacy/features/guidance/types/guidanceTypes'

type Props = {
  scriptData: GuidanceScriptData
  onSave?: (
    scriptSelections: Record<string, boolean>,
    scriptOrder: string[]
  ) => Promise<void>
  onCancel?: () => void
}

const GuidanceScriptTab: React.FC<Props> = ({
  scriptData,
  onSave,
  onCancel,
}) => {
  const [selectedScripts, setSelectedScripts] = useState<GuidanceScript[]>([])
  const [scriptItems, setScriptItems] = useState(scriptData.script_items)
  const [isSaving, setIsSaving] = useState(false)
  const [isPhrasesExpanded, setIsPhrasesExpanded] = useState(true)

  // 初期選択状態を設定（script_itemsの内容が実際に変更された場合のみ）
  useEffect(() => {
    const initialSelected: GuidanceScript[] = []

    scriptData.script_items.forEach((item) => {
      item.scripts.forEach((script) => {
        if (script.isSelected) {
          initialSelected.push(script)
        }
      })
    })

    setSelectedScripts(initialSelected)
    setScriptItems(scriptData.script_items)
  }, [scriptData.script_items])

  // 選択状態と順序の変更を検知（サーバーデータとの比較）
  const hasChanges = useMemo(() => {
    const serverSelectedScripts = scriptData.script_items
      .flatMap((item) => item.scripts)
      .filter((script) => script.isSelected)

    const serverSelectedIds = serverSelectedScripts.map((script) => script.id)
    const currentIds = selectedScripts.map((s) => s.id)

    // 選択状態の変更をチェック
    const selectionChanged =
      currentIds.length !== serverSelectedIds.length ||
      currentIds.some((id) => !serverSelectedIds.includes(id)) ||
      serverSelectedIds.some((id) => !currentIds.includes(id))

    // 順序の変更をチェック（選択が同じ場合のみ）
    let orderChanged = false
    if (!selectionChanged && currentIds.length > 0) {
      orderChanged = currentIds.some(
        (id, index) => id !== serverSelectedIds[index]
      )
    }

    return selectionChanged || orderChanged
  }, [selectedScripts, scriptData])

  const handleScriptToggle = (script: GuidanceScript) => {
    const isSelected = selectedScripts.some((s) => s.id === script.id)

    if (isSelected) {
      setSelectedScripts(selectedScripts.filter((s) => s.id !== script.id))
    } else {
      setSelectedScripts([...selectedScripts, script])
    }

    // scriptItemsの選択状態も更新
    setScriptItems(
      scriptItems.map((item) => ({
        ...item,
        scripts: item.scripts.map((s) =>
          s.id === script.id ? { ...s, isSelected: !isSelected } : s
        ),
      }))
    )
  }

  const handleRemoveSelected = (scriptId: string) => {
    const script = selectedScripts.find((s) => s.id === scriptId)
    if (script) {
      handleScriptToggle(script)
    }
  }

  // ドラッグ&ドロップによる順序変更
  const handleDragEnd = (result: DropResult) => {
    // ドロップ先がない、または同じ位置の場合は何もしない
    if (
      !result.destination ||
      result.source.index === result.destination.index
    ) {
      return
    }

    const reorderedScripts = Array.from(selectedScripts)
    const [reorderedItem] = reorderedScripts.splice(result.source.index, 1)
    reorderedScripts.splice(result.destination.index, 0, reorderedItem)

    setSelectedScripts(reorderedScripts)
  }

  // 上下移動ボタンによる順序変更
  const handleMoveUp = (index: number) => {
    if (index === 0) return
    const reorderedScripts = Array.from(selectedScripts)
    const [item] = reorderedScripts.splice(index, 1)
    reorderedScripts.splice(index - 1, 0, item)
    setSelectedScripts(reorderedScripts)
  }

  const handleMoveDown = (index: number) => {
    if (index === selectedScripts.length - 1) return
    const reorderedScripts = Array.from(selectedScripts)
    const [item] = reorderedScripts.splice(index, 1)
    reorderedScripts.splice(index + 1, 0, item)
    setSelectedScripts(reorderedScripts)
  }

  const handleClearAll = () => {
    setSelectedScripts([])
    setScriptItems(
      scriptItems.map((item) => ({
        ...item,
        scripts: item.scripts.map((s) => ({ ...s, isSelected: false })),
      }))
    )
  }

  const handleRevert = () => {
    const revertedScripts: GuidanceScript[] = []

    // サーバーデータの状態に戻す
    const revertedItems = scriptData.script_items.map((item) => ({
      ...item,
      scripts: item.scripts.map((script) => {
        if (script.isSelected) {
          revertedScripts.push(script)
        }
        return { ...script }
      }),
    }))

    setScriptItems(revertedItems)
    setSelectedScripts(revertedScripts)
  }

  const handleSave = async () => {
    if (!onSave) return

    setIsSaving(true)
    try {
      // 全スクリプトの選択状態を作成
      const scriptSelections: Record<string, boolean> = {}
      scriptData.script_items.forEach((item) => {
        item.scripts.forEach((script) => {
          scriptSelections[script.id] = selectedScripts.some(
            (s) => s.id === script.id
          )
        })
      })

      // 選択されたスクリプトの順序を作成
      const scriptOrder = selectedScripts.map((s) => s.id)

      await onSave(scriptSelections, scriptOrder)
      // 保存後はサーバーから最新データを再取得するため、
      // ローカル状態の更新は不要（親コンポーネントで再取得される）
    } catch (_error) {
      // エラーは親コンポーネントでハンドリングされる
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* 選択されたフレーズエリア */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          ■ 選択されたフレーズ
          <Chip label={selectedScripts.length} size="small" color="primary" />
        </Typography>

        {selectedScripts.length > 0 ? (
          <Box
            sx={{
              backgroundColor: colorTokens.special.highlight,
              border: `1px solid ${colorTokens.blue[50]}`,
              borderRadius: 1,
              p: 2,
              mb: 2,
            }}
          >
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="selected-scripts">
                {(provided, snapshot) => (
                  <List
                    dense
                    disablePadding
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{
                      backgroundColor: snapshot.isDraggingOver
                        ? colorTokens.blue[50]
                        : 'transparent',
                      borderRadius: 1,
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    {selectedScripts.map((script, index) => (
                      <Draggable
                        key={script.id}
                        draggableId={script.id}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <ListItem
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            sx={{
                              py: 0.5,
                              pr: 1,
                              backgroundColor: snapshot.isDragging
                                ? colorTokens.background.paper
                                : 'transparent',
                              boxShadow: snapshot.isDragging
                                ? '0 4px 8px rgba(0,0,0,0.1)'
                                : 'none',
                              borderRadius: snapshot.isDragging ? 1 : 0,
                              '&:hover': {
                                backgroundColor: snapshot.isDragging
                                  ? colorTokens.background.paper
                                  : 'rgba(0, 0, 0, 0.02)',
                              },
                            }}
                          >
                            <Box
                              {...provided.dragHandleProps}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                mr: 1,
                                cursor: 'grab',
                                borderRadius: 1,
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                                '&:active': {
                                  cursor: 'grabbing',
                                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                },
                              }}
                            >
                              <DragIndicatorIcon
                                fontSize="small"
                                sx={{ color: colorTokens.text.secondary }}
                              />
                            </Box>
                            <ListItemText
                              primary={script.text}
                              primaryTypographyProps={{
                                color: colorTokens.grey[800],
                              }}
                            />
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleMoveUp(index)}
                                disabled={index === 0}
                                sx={{ p: 0.5 }}
                              >
                                <KeyboardArrowUpIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveDown(index)}
                                disabled={index === selectedScripts.length - 1}
                                sx={{ p: 0.5 }}
                              >
                                <KeyboardArrowDownIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveSelected(script.id)}
                                sx={{ p: 0.5 }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </ListItem>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </List>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            フレーズが選択されていません
          </Alert>
        )}

        {/* アクションボタン */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearAll}
              disabled={selectedScripts.length === 0}
              sx={{
                minHeight: 28,
                py: 0.5,
                px: 1.5,
                color: 'text.primary',
                borderColor: 'text.secondary',
                '&:hover': {
                  borderColor: 'text.primary',
                  backgroundColor: 'action.hover',
                },
                '&:disabled': {
                  opacity: 0.5,
                },
              }}
            >
              クリア
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleRevert}
              disabled={!hasChanges}
              sx={{
                minHeight: 28,
                py: 0.5,
                px: 1.5,
                color: 'text.primary',
                borderColor: 'text.secondary',
                '&:hover': {
                  borderColor: 'text.primary',
                  backgroundColor: 'action.hover',
                },
                '&:disabled': {
                  opacity: 0.5,
                },
              }}
            >
              元に戻す
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
            >
              {isSaving ? '保存中...' : '保存'}
            </Button>
            {onCancel && (
              <Button variant="outlined" size="small" onClick={onCancel}>
                キャンセル
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* フレーズ案セクション */}
      <Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 2,
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
            },
            transition: 'opacity 0.2s ease',
          }}
          onClick={() => setIsPhrasesExpanded(!isPhrasesExpanded)}
        >
          <Typography
            variant="subtitle1"
            sx={{
              color: colorTokens.text.primary,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            ■ フレーズ案
            <Typography
              component="span"
              sx={{
                color: colorTokens.text.secondary,
                display: 'inline-flex',
                alignItems: 'center',
                ml: 0.5,
              }}
            >
              {isPhrasesExpanded ? '▼' : '▶'}
            </Typography>
          </Typography>
        </Box>

        <Collapse in={isPhrasesExpanded}>
          <Box>
            {scriptItems.map((item, itemIndex) => (
              <Box key={itemIndex} sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1,
                    color: colorTokens.text.secondary,
                    pl: 2,
                  }}
                >
                  ▪ {item.title}
                </Typography>
                <List dense disablePadding>
                  {item.scripts.map((script) => (
                    <ListItem
                      key={script.id}
                      sx={{
                        py: 0.5,
                        pl: 2,
                        pr: 0,
                        '&:hover': {
                          backgroundColor: colorTokens.grey[100],
                          cursor: 'pointer',
                        },
                      }}
                      onClick={() => handleScriptToggle(script)}
                    >
                      <Checkbox
                        checked={script.isSelected || false}
                        sx={{ mr: 1 }}
                      />
                      <ListItemText
                        primary={script.text}
                        primaryTypographyProps={{
                          lineHeight: 1.6,
                          color: script.isSelected
                            ? colorTokens.blue[600]
                            : colorTokens.grey[800],
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>
    </Box>
  )
}

export default GuidanceScriptTab
